import clsx from "clsx";
import {
  memo,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FiChevronDown } from "react-icons/fi";
import { MdBlock } from "react-icons/md";
import noImage from "@/assets/img/no-image.svg";
import { BOARD_CONFIG, IS_MIX_CATALOG } from "@/config/browserBoard";
import type { Thread } from "@/entities/thread";
import {
  getImageUrl,
  getThreadBodyText,
  getThreadTitle,
  isR18ThreadTagName,
  isThreadExpired,
} from "@/entities/thread";
import { ThreadContextMenu } from "@/features/catalog/components/actions/ThreadContextMenu";
import { CatalogBoardIcon } from "@/features/catalog/components/CatalogBoardIcon";
import { CatalogHoverPreview } from "@/features/catalog/components/desktop/CatalogHoverPreview";
import { CatalogThreadLink } from "@/features/catalog/components/navigation/CatalogThreadLink";
import { getThreadLinkAttributes } from "@/features/catalog/hooks/useFilteredThreads";
import {
  type CatalogEvent,
  isCatalogEvent,
} from "@/features/catalog/mediators/catalogEvents";
import {
  createCatalogItemMediator,
  isCatalogItemEvent,
} from "@/features/catalog/mediators/catalogItemMediator";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import {
  getCatalogImageMaskKind,
  getCatalogMaskVisibility,
} from "@/features/catalog/utils/catalogReveal";
import { getCatalogCategoryTags } from "@/features/catalog/utils/catalogTags";
import { useFavStore } from "@/features/fav-filter/stores/favStore";
import {
  unreadReplyCount,
  useHistoryStore,
  useViewedHistoryItem,
} from "@/features/history/stores/historyStore";
import { useNgStore } from "@/features/ng-filter/stores";
import { useSettingsStore } from "@/features/settings/hooks";
import { usePrefetchThreadState } from "@/features/thread/hooks/usePrefetchThreadState";
import { resolveThumbnailBadge } from "@/shared/lib";
import { useAimogeBeforeRender, useAimogeRendered } from "@/shared/lib/aimoge";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
import { OekakiBadge, VideoBadge } from "@/shared/ui/media";
import { NewsArticleImage } from "@/shared/ui/media/NewsArticleImage";
import { TagBadges, TextEffects } from "@/shared/ui/navigation";

/**
 * カタログ共通設定(useCatalogStore)より優先して使いたい値をまとめて指定する。
 * 呼び出し元のカタログ設定に依存せず固定表示を維持したい文脈
 * (例: 閲覧履歴サイドバーでの旧実装互換表示)で使う。
 */
export interface CatalogItemOverrides {
  /** 画像サイズ(px)。useCatalogStore.imageSize を上書きする。 */
  imageSize?: number;
  /** 本文表示行数。useCatalogStore.textLines を上書きする。 */
  textLines?: number;
  /** 新規タブで開くか。useCatalogStore.openInNewTab を上書きする。 */
  openInNewTab?: boolean;
  /** 未読バッジ表示。useCatalogStore.showUnreadCount を上書きする。 */
  showUnreadCount?: boolean;
  /** レス数表示。useCatalogStore.showCount を上書きする。 */
  showCount?: boolean;
  /** タグ用フッターを表示するか。通常カタログでは既定で表示する。 */
  showFooter?: boolean;
  /**
   * trueの場合、クリック時の閲覧履歴記録(addViewed)を行わない。
   * 遷移先(DesktopThreadView等)が別途addViewedを呼ぶ前提で、
   * 表示中の一覧がクリック直後に並び替わる(再フェッチが走る)のを防ぐ。
   * NG/R18マスク解除はこのフラグの影響を受けない。
   */
  disableViewedTracking?: boolean;
}

interface Props {
  thread: Thread;
  isNew: boolean;
  overrides?: CatalogItemOverrides;
}

/**
 * hover intentの遅延(ms)。カタログをマウスで素通りしただけの走査で通過セル
 * 全てにprefetchが連続発火しないための猶予。TanStack Routerの
 * `defaultPreloadDelay`(既定50ms)の思想に合わせつつ、hoverはクリックより
 * 誤爆コストが低いため、ここではやや長めの150msを採る。
 */
const HOVER_INTENT_DELAY_MS = 150;

/** 旧カタログの1セル。モバイルのCatalogItemとはDOM/寸法を分離する。 */
export const DesktopCatalogItem: React.FunctionComponent<Props> = memo(
  function DesktopCatalogItem(props: Props) {
    const renderedThread = useAimogeBeforeRender(
      "catalog:beforeRender",
      props.thread,
    );
    const elementRef = useAimogeRendered<HTMLElement>(
      "catalog",
      renderedThread,
      renderedThread?.id,
    );

    if (!renderedThread) return null;
    return (
      <DesktopCatalogItemPresenter
        thread={renderedThread}
        isNew={props.isNew}
        overrides={props.overrides}
        elementRef={elementRef}
      />
    );
  },
);

interface ContentProps extends Props {
  elementRef: RefObject<HTMLElement | null>;
}

const DesktopCatalogItemPresenter: React.FunctionComponent<ContentProps> = memo(
  function DesktopCatalogItemPresenter({
    thread,
    isNew,
    overrides,
    elementRef,
  }: ContentProps) {
    const showNew = useCatalogStore((state) => state.showNew);
    const catalogShowCount = useCatalogStore((state) => state.showCount);
    const showCount = overrides?.showCount ?? catalogShowCount;
    const showFooter = overrides?.showFooter ?? true;
    const catalogShowUnreadCount = useCatalogStore(
      (state) => state.showUnreadCount,
    );
    const showUnreadCount =
      overrides?.showUnreadCount ?? catalogShowUnreadCount;
    const catalogTextLines = useCatalogStore((state) => state.textLines);
    const textLines = overrides?.textLines ?? catalogTextLines;
    const catalogImageSize = useCatalogStore((state) => state.imageSize);
    const imageSize = overrides?.imageSize ?? catalogImageSize;
    const catalogOpenInNewTab = useCatalogStore((state) => state.openInNewTab);
    const openInNewTab = overrides?.openInNewTab ?? catalogOpenInNewTab;
    const threadLinkAttributes = getThreadLinkAttributes(openInNewTab);
    const catalogAnim = useCatalogStore((state) => state.catalogAnim);
    const catalogHoverPreview = useCatalogStore(
      (state) => state.catalogHoverPreview,
    );
    const showR18 = useSettingsStore((state) => state.showR18);
    const extendedTextEffects = useSettingsStore(
      (state) => state.extendedTextEffects,
    );
    const boardSlug = thread.b ?? BOARD_CONFIG.boardKey;
    const addViewed = useHistoryStore((state) => state.addViewed);
    const viewedItem = useViewedHistoryItem(boardSlug, thread.id);
    const isSuperFavorite = useFavStore(
      (state) => state.isThreadFavorite(thread) === 2,
    );
    const { isThreadHidden, showNgContent } = useNgStore();
    const prefetchThreadState = usePrefetchThreadState();
    const isNg = showNgContent && isThreadHidden(thread);
    const isR18 = thread.tags.some((tag) => isR18ThreadTagName(tag.name));
    const itemModel = useMemo(
      () => ({
        threadId: thread.id,
        boardSlug,
        isNg,
        isR18,
        hasAttachment: Boolean(thread.opPost.attachment || thread.opPost.news),
        showR18,
        isViewed: viewedItem !== undefined,
        trackViewed: !overrides?.disableViewedTracking,
        prefetchAvailable: thread.b === undefined || IS_MIX_CATALOG,
        menuOpenMethod: "menu-button" as const,
        hasFinePointer: true,
      }),
      [
        boardSlug,
        isNg,
        isR18,
        overrides?.disableViewedTracking,
        showR18,
        thread.b,
        thread.id,
        thread.opPost.attachment,
        thread.opPost.news,
        viewedItem,
      ],
    );
    const [mediator] = useState(() =>
      createCatalogItemMediator(itemModel, (effect) => {
        if (effect.type === "mark-viewed") {
          addViewed(effect.boardSlug, effect.threadId);
        } else {
          prefetchThreadState(effect.threadId);
        }
      }),
    );
    useHierarchyLifetime(mediator);
    const node = useComponentNode((event) => {
      if (!isCatalogEvent(event) || !isCatalogItemEvent(event)) {
        return "unhandled";
      }
      return mediator.dispatch(event).status === "handled"
        ? "handled"
        : "unhandled";
    });
    const itemState = useMediatorSnapshot(mediator);
    const notify = (event: CatalogEvent): void => {
      node.dispatch(event);
    };
    const menuOpen = itemState.isMenuOpen;
    const ngRevealed = itemState.isNgRevealed;
    const r18Revealed = itemState.isR18Revealed;
    const isHovered = itemState.isHovered;
    const accessibleTitle = getThreadTitle(thread);
    const displayBody = getThreadBodyText(thread);
    const categoryTags = getCatalogCategoryTags(thread.tags);
    const isR18Hidden = itemState.isR18Hidden;
    const maskVisibility = getCatalogMaskVisibility({
      isNg,
      ngRevealed,
      isR18Hidden,
    });
    const maskKind = getCatalogImageMaskKind({
      isNg,
      ngRevealed,
      isR18Hidden,
    });
    const isViewed = itemState.isViewed;
    const isExpired = isThreadExpired(thread);
    const unreadCount = viewedItem
      ? (unreadReplyCount(viewedItem.readReplyNumber, thread.replyCount) ?? 0)
      : 0;
    const newsImage = thread.opPost.attachment ? undefined : thread.opPost.news;
    const attachmentImageUrl = getImageUrl(
      thread.opPost.attachment,
      (catalogAnim === "always" || (catalogAnim === "hover" && isHovered)) &&
        thread.opPost.attachment?.kind === "animated",
    );
    const imageUrl = newsImage?.imageUrl ?? attachmentImageUrl;
    const thumbnailBadge = resolveThumbnailBadge(thread.opPost.attachment);
    const isVideo = thumbnailBadge === "video";
    const isOekaki = thumbnailBadge === "oekaki";

    useLayoutEffect(() => {
      mediator.dispatch({
        type: "catalog/item-synchronized",
        ...itemModel,
      });
    }, [itemModel, mediator]);

    return (
      <ComponentScope node={node}>
        <DesktopCatalogItemView
          thread={thread}
          isNew={isNew}
          elementRef={elementRef}
          accessibleTitle={accessibleTitle}
          imageSize={imageSize}
          isViewed={isViewed}
          isSuperFavorite={isSuperFavorite}
          isExpired={isExpired}
          maskKind={maskKind}
          maskVisibility={maskVisibility}
          imageUrl={imageUrl}
          newsImage={newsImage}
          isVideo={isVideo}
          isOekaki={isOekaki}
          showNew={showNew}
          textLines={textLines}
          extendedTextEffects={extendedTextEffects}
          displayBody={displayBody}
          showCount={showCount}
          showUnreadCount={showUnreadCount}
          unreadCount={unreadCount}
          threadLinkAttributes={threadLinkAttributes}
          catalogHoverPreview={catalogHoverPreview}
          openInNewTab={openInNewTab}
          showFooter={showFooter}
          categoryTags={categoryTags}
          menuOpen={menuOpen}
          ngRevealed={ngRevealed}
          r18Revealed={r18Revealed}
          isR18Hidden={isR18Hidden}
          onEvent={notify}
        />
      </ComponentScope>
    );
  },
);

export interface DesktopCatalogItemViewProps {
  readonly thread: Thread;
  readonly isNew: boolean;
  readonly elementRef: RefObject<HTMLElement | null>;
  readonly accessibleTitle: string;
  readonly imageSize: number;
  readonly isViewed: boolean;
  readonly isSuperFavorite: boolean;
  readonly isExpired: boolean;
  readonly maskKind: ReturnType<typeof getCatalogImageMaskKind>;
  readonly maskVisibility: ReturnType<typeof getCatalogMaskVisibility>;
  readonly imageUrl: string;
  readonly newsImage: Thread["opPost"]["news"];
  readonly isVideo: boolean;
  readonly isOekaki: boolean;
  readonly showNew: boolean;
  readonly textLines: number;
  readonly extendedTextEffects: boolean;
  readonly displayBody: string;
  readonly showCount: boolean;
  readonly showUnreadCount: boolean;
  readonly unreadCount: number;
  readonly threadLinkAttributes: ReturnType<typeof getThreadLinkAttributes>;
  readonly catalogHoverPreview: boolean;
  readonly openInNewTab: boolean;
  readonly showFooter: boolean;
  readonly categoryTags: ReturnType<typeof getCatalogCategoryTags>;
  readonly menuOpen: boolean;
  readonly ngRevealed: boolean;
  readonly r18Revealed: boolean;
  readonly isR18Hidden: boolean;
  readonly onEvent: (event: CatalogEvent) => void;
}

export function DesktopCatalogItemView({
  thread,
  isNew,
  elementRef,
  accessibleTitle,
  imageSize,
  isViewed,
  isSuperFavorite,
  isExpired,
  maskKind,
  maskVisibility,
  imageUrl,
  newsImage,
  isVideo,
  isOekaki,
  showNew,
  textLines,
  extendedTextEffects,
  displayBody,
  showCount,
  showUnreadCount,
  unreadCount,
  threadLinkAttributes,
  catalogHoverPreview,
  openInNewTab,
  showFooter,
  categoryTags,
  menuOpen,
  ngRevealed,
  r18Revealed,
  isR18Hidden,
  onEvent,
}: DesktopCatalogItemViewProps): React.ReactElement {
  const hoverIntentTimerRef = useRef<number | null>(null);
  const cardLinkRef = useRef<HTMLAnchorElement>(null);
  const thumbnailRef = useRef<HTMLElement>(null);
  const r18TriggerRef = useRef<HTMLButtonElement>(null);
  const pendingFocusRef = useRef<"card" | "r18" | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const clearHoverIntentTimer = (): void => {
    if (hoverIntentTimerRef.current === null) return;
    window.clearTimeout(hoverIntentTimerRef.current);
    hoverIntentTimerRef.current = null;
  };
  useEffect(() => {
    return (): void => {
      if (hoverIntentTimerRef.current === null) return;
      window.clearTimeout(hoverIntentTimerRef.current);
      hoverIntentTimerRef.current = null;
    };
  }, []);
  useEffect(() => {
    if (pendingFocusRef.current === "card" && r18Revealed) {
      pendingFocusRef.current = null;
      cardLinkRef.current?.focus();
    } else if (
      pendingFocusRef.current === "r18" &&
      maskKind === "r18" &&
      ngRevealed
    ) {
      pendingFocusRef.current = null;
      r18TriggerRef.current?.focus();
    }
  }, [maskKind, ngRevealed, r18Revealed]);
  const onClick = (event: React.MouseEvent): void => {
    if (maskKind === "ng") {
      event.preventDefault();
      event.stopPropagation();
      if (isR18Hidden) pendingFocusRef.current = "r18";
    }
    onEvent({ type: "catalog/item-activated" });
  };
  const onR18Reveal = (): void => {
    pendingFocusRef.current = "card";
    onEvent({ type: "catalog/item-r18-revealed" });
  };
  const onHoverIntent = (): void => {
    onEvent({ type: "catalog/item-hover-changed", hovered: true });
    clearHoverIntentTimer();
    hoverIntentTimerRef.current = window.setTimeout(() => {
      hoverIntentTimerRef.current = null;
      onEvent({ type: "catalog/item-prefetch-requested" });
    }, HOVER_INTENT_DELAY_MS);
  };
  const onHoverEnd = (): void => {
    onEvent({ type: "catalog/item-hover-changed", hovered: false });
    clearHoverIntentTimer();
  };
  return (
    <article
      ref={elementRef}
      data-catalog-layout-key={thread.id}
      aria-label={accessibleTitle}
      className={clsx(
        "desktop-catalog-item",
        isViewed && "desktop-catalog-item-viewed",
        isSuperFavorite && "catalog-item-super-favorite",
        isExpired && "desktop-catalog-item-expired",
      )}
      style={
        {
          "--catalog-image-size": `${imageSize}px`,
        } as React.CSSProperties
      }
    >
      {maskKind === "r18" && (
        <button
          ref={r18TriggerRef}
          type="button"
          className="desktop-catalog-r18-trigger"
          aria-label={`${accessibleTitle}のR18画像を表示`}
          onClick={onR18Reveal}
          onMouseEnter={onHoverIntent}
          onMouseLeave={onHoverEnd}
          onFocus={onHoverIntent}
          onBlur={onHoverEnd}
        />
      )}
      <CatalogThreadLink
        ref={cardLinkRef}
        thread={thread}
        onClick={onClick}
        {...threadLinkAttributes}
        aria-label={
          maskKind === "ng"
            ? `${accessibleTitle}のNG画像を表示`
            : accessibleTitle
        }
        onMouseEnter={onHoverIntent}
        onMouseLeave={onHoverEnd}
        onFocus={onHoverIntent}
        onBlur={onHoverEnd}
      >
        <figure
          ref={thumbnailRef}
          className={clsx(
            "desktop-catalog-thumb",
            maskVisibility.image && !maskVisibility.text && "relative",
          )}
        >
          {newsImage ? (
            <NewsArticleImage
              news={newsImage}
              alt={accessibleTitle}
              loading="lazy"
              decoding="async"
              className={clsx(
                maskKind === "ng" && "blur-xl opacity-20",
                maskKind === "r18" && "r18-spoiler-image",
              )}
            />
          ) : (
            <img
              src={imageUrl}
              alt=""
              loading="lazy"
              decoding="async"
              className={clsx(
                maskKind === "ng" && "blur-xl opacity-20",
                maskKind === "r18" && "r18-spoiler-image",
              )}
              onError={(
                event: React.SyntheticEvent<HTMLImageElement>,
              ): void => {
                event.currentTarget.src = noImage;
              }}
            />
          )}
          {maskKind === "ng" && (
            <strong className="desktop-r18-mask" aria-hidden="true">
              <MdBlock aria-hidden="true" size={16} /> NG
            </strong>
          )}
          {isVideo && !maskVisibility.image && <VideoBadge />}
          {isOekaki && maskKind !== "ng" && <OekakiBadge />}
          {showNew && isNew && !maskVisibility.image && (
            <strong className="desktop-catalog-badge desktop-catalog-badge-new">
              NEW
            </strong>
          )}
        </figure>
        {textLines > 0 && (
          <p
            className={clsx(
              "desktop-catalog-text catalog-text-clamp",
              maskVisibility.text && "blur-sm",
            )}
            style={
              {
                "--catalog-text-lines": textLines,
              } as React.CSSProperties
            }
          >
            {extendedTextEffects ? (
              <TextEffects text={displayBody} extended />
            ) : (
              displayBody
            )}
          </p>
        )}
        <p
          className={clsx(
            "desktop-catalog-count flex items-center gap-1",
            maskVisibility.text && "blur-sm",
          )}
        >
          <data value={thread.replyCount}>
            {showCount && thread.replyCount}
            {showUnreadCount && unreadCount > 0 && (
              <strong className="catalog-unread-count ml-1 text-xs">
                +{unreadCount}
              </strong>
            )}
          </data>
          <CatalogBoardIcon slug={thread.b} className="ml-auto" />
        </p>
      </CatalogThreadLink>
      {/* 設定OFF時はマウントせず、イベントリスナーも一切登録しない。
            NG/R18マスク中はマスクを迂回してしまうため出さない。 */}
      {catalogHoverPreview && !maskVisibility.image && (
        <CatalogHoverPreview
          thread={thread}
          imageUrl={imageUrl}
          anchorRef={cardLinkRef}
          previewAnchorRef={thumbnailRef}
          onClick={onClick}
          openInNewTab={openInNewTab}
        />
      )}
      {showFooter && (
        <footer className="desktop-catalog-footer">
          <TagBadges
            tags={categoryTags}
            className="catalog-tags desktop-catalog-tags"
            r18First
            shouldRenderWhenEmpty
          />
        </footer>
      )}
      <button
        ref={menuButtonRef}
        type="button"
        className="desktop-catalog-menu"
        aria-label="スレッドメニュー"
        aria-expanded={menuOpen}
        onClick={(): void => onEvent({ type: "catalog/item-menu-toggled" })}
      >
        <FiChevronDown aria-hidden="true" />
      </button>
      <ThreadContextMenu
        thread={thread}
        isOpen={menuOpen}
        onClose={(): void => onEvent({ type: "catalog/item-menu-closed" })}
        anchorRef={menuButtonRef}
      />
    </article>
  );
}
